import { Suspense, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, TouchableOpacity, View } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  interpolateColor,
  measure,
  type SharedValue,
  useAnimatedRef,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { runOnUI } from 'react-native-worklets'

import { Icon } from '@/src/elements/media/Icon'
import { Modal } from '@/src/elements/modal/Modal'
import { Placeholder, PlaceholderLine } from '@/src/elements/placeholder'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import type { TGetCommunityApiData } from '@/src/features/community/api/getCommunity.types'
import { ResponsiveDimensions } from '@/src/general/components/ResponsiveDimensions'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { lazyWithRetries } from '@/src/general/utils/lazyWithRetries'
import { colors } from '@/src/styles/colors'

import { useHeaderTranslateY } from '../../../hooks/useListAnimatedScroll'

const Content = lazyWithRetries(() =>
  import('@/src/features/publication/ui/screens/PublicationScreen/components/ProxyContent').then(
    ({ ProxyContent }) => ({ default: ProxyContent }),
  ),
)

const DescriptionPlaceholder = () => (
  <Placeholder className="flex-col gap-4 bg-white">
    {Array.from({ length: 7 }, (_, index) => (
      <PlaceholderLine key={index} />
    ))}
  </Placeholder>
)

const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity)
const AnimatedTypography = Animated.createAnimatedComponent(Typography)

type THeaderTitleProps = {
  community: TGetCommunityApiData
  offsetY: SharedValue<number>
  opacityStyle?: ReturnType<typeof useAnimatedStyle>
  animated?: boolean
}

const HEADER_ANIMATION_THRESHOLD = 280
const FALLBACK_TRANSLATE_Y = 108
const TRANSLATE_Y_OFFSET = Platform.select({ default: 132, web: -34 })
const TRANSLATE_X_OFFSET = Platform.select({ default: 54, web: 60 })
const INTERPOLATED_FONT_WEIGHTS = ['400', '500', '600', '700', '800', '900'] as const

export const HeaderTitle = ({ community, offsetY, opacityStyle, animated }: THeaderTitleProps) => {
  const [showDescription, setShowDescription] = useState(false)
  const aTitleRef = useAnimatedRef()
  const windowWidth = useDimensions().width
  const titleRestBottom = useSharedValue<number | null>(null)
  const appliedTranslateY = useSharedValue(0)
  const translateYOffset = useHeaderTranslateY()
  const { top } = useSafeAreaInsets()
  const titleContainerAnimatedStyle = useAnimatedStyle(() => {
    const restBottom = titleRestBottom.get()
    // When collapsed, the whole header is translated by offsetY (-280), so
    // placing the title bottom at 135 in the window means: 135 - (restBottom - 280).
    const collapsedTitleY = top + TRANSLATE_Y_OFFSET
    const translateYTarget =
      restBottom === null ? FALLBACK_TRANSLATE_Y : collapsedTitleY - (restBottom - translateYOffset)

    const translateY = interpolate(offsetY.get(), [-translateYOffset, 0], [translateYTarget, 0], Extrapolation.CLAMP)
    appliedTranslateY.set(translateY)
    return {
      width: interpolate(offsetY.get(), [-translateYOffset, 0], [windowWidth * 0.7, windowWidth], Extrapolation.CLAMP),
      transform: [
        {
          translateX: interpolate(offsetY.get(), [-translateYOffset, 0], [TRANSLATE_X_OFFSET, 0], Extrapolation.CLAMP),
        },
        { translateY },
      ],
    }
  }, [community?.id])

  const titleAnimatedStyle = useAnimatedStyle(() => {
    return {
      lineHeight: interpolate(offsetY.get(), [-HEADER_ANIMATION_THRESHOLD, 0], [24, 32], Extrapolation.CLAMP),
      fontWeight:
        INTERPOLATED_FONT_WEIGHTS[
          (Math.round(
            interpolate(offsetY.get(), [-HEADER_ANIMATION_THRESHOLD, 0], [400, 900], Extrapolation.CLAMP) / 100,
          ) *
            100 -
            400) /
            100
        ],
      fontSize: interpolate(offsetY.get(), [-translateYOffset, 0], [17, 24], Extrapolation.CLAMP),
      color: interpolateColor(
        offsetY.get(),
        [-translateYOffset, -120],
        [colors.default['text-contrast-default'].DEFAULT, 'white'],
      ),
    }
  })

  const measureTitle = () => {
    runOnUI(() => {
      'worklet'
      // measure() reflects the transforms currently applied to the view (the
      // header's translateY, driven by the same offsetY, and this container's
      // own), and mid-transition those may not include the latest offsetY yet
      // depending on mapper execution order. So only measure at rest, and
      // derive the collapsed position from that measurement below.
      // oxlint-disable-next-line max-params
      const measurement = measure(aTitleRef)
      if (measurement == null) return
      if (titleRestBottom.get() !== null) return
      titleRestBottom.set(measurement.pageY + measurement.height)
    })()
  }

  return (
    <ResponsiveDimensions>
      {({ xs, lessThan }) => (
        <>
          <Animated.View
            ref={aTitleRef}
            onLayout={measureTitle}
            testID={TEST_ID.MISCELLANEOUS.CONTAINER_STYLE}
            style={[animated ? titleContainerAnimatedStyle : undefined, { maxWidth: '100%' }]}
            className="h-24 justify-center web:h-16"
          >
            <AnimatedTypography
              style={animated ? titleAnimatedStyle : undefined}
              heading="h1"
              colors="white"
              className="web:font-black max-w-full"
              size={xs ? 'xl' : '2xl'}
              numberOfLines={2}
            >
              {community.name}
            </AnimatedTypography>
          </Animated.View>

          <AnimatedTouchable
            style={[opacityStyle, { maxWidth: '100%' }]}
            className="md:px-6"
            onPress={() => setShowDescription(true)}
          >
            <View className="flex-row w-full items-center">
              <Typography colors="white" contrast="low" className="flex-1" numberOfLines={1}>
                {community.introduction}
              </Typography>
              <View className="ml-2 flex-row">
                <Typography colors="white" contrast="low" bold className="underline">
                  <FormattedMessage id="community.description.read-more" />
                </Typography>
                <Icon name={ICON.CHEVRON_DOWN} className="color-white self-end" size="sm" />
              </View>
            </View>
          </AnimatedTouchable>

          <Modal
            showModal={showDescription}
            title={<FormattedMessage id="community.tab.description" />}
            onCancel={() => setShowDescription(false)}
            scrollable
            classNames={{
              body: lessThan.md ? '' : 'p-9',
              base: lessThan.md ? 'w-full' : 'max-w-[600px]',
            }}
            style={lessThan.md ? undefined : { maxWidth: 600 }}
            containerStyle={lessThan.md ? undefined : { maxWidth: 600 }}
          >
            <Suspense fallback={<DescriptionPlaceholder />}>
              <Typography>{community.introduction}</Typography>
              <Spacer y={4} />
              <Content content={community.description} />
            </Suspense>
          </Modal>
        </>
      )}
    </ResponsiveDimensions>
  )
}
